import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, HelpCircle, Loader2, ShieldAlert, Unlock } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/finance`;

const TONE = {
  unknown: { box: 'bg-gray-50 border-gray-200', text: 'text-gray-700', Icon: HelpCircle },
  negative: { box: 'bg-red-50 border-red-200', text: 'text-red-700', Icon: ShieldAlert },
  below_min: { box: 'bg-amber-50 border-amber-200', text: 'text-amber-800', Icon: AlertTriangle },
  warning: { box: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-800', Icon: AlertTriangle },
  ok: { box: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', Icon: CheckCircle2 },
  own_fleet: { box: 'bg-blue-50 border-blue-200', text: 'text-blue-700', Icon: CheckCircle2 },
};

const fcfa = (v) => `${Number(v || 0).toLocaleString('fr-FR')} FCFA`;

/** Bandeau de marge d'une opération (Lot A) — visible uniquement pour les rôles financiers. */
export const OperationMarginBanner = ({ operationId, canOverride, onChanged, refreshToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!operationId) return;
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/operations/${operationId}/margin`);
      setData(res);
    } catch (err) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [operationId, refreshToken]);

  useEffect(() => { load(); }, [load]);

  const submitOverride = async () => {
    if (reason.trim().length < 15) {
      toast.error('Motif trop court (15 caractères minimum)');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/operations/${operationId}/margin/override`, { reason: reason.trim() });
      toast.success('Dérogation enregistrée');
      setDialogOpen(false);
      setReason('');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Dérogation impossible');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500 py-2" data-testid="margin-banner-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Calcul de la marge…
      </div>
    );
  }
  if (!data) return null;

  const tone = TONE[data.status] || TONE.unknown;
  const { Icon } = tone;
  const needsAttention = ['unknown', 'negative', 'below_min'].includes(data.status);

  return (
    <div className={`rounded-lg border px-3 py-2.5 ${tone.box}`} data-testid="operation-margin-banner">
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${tone.text}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className={`text-sm font-semibold ${tone.text}`} data-testid="margin-banner-status">
              {data.status === 'unknown'
                ? 'Marge inconnue'
                : `Marge ${fcfa(data.marginAmount)} (${data.marginPct} %)`}
            </p>
            {data.tier && (
              <Badge variant="outline" className="text-[10px]">
                Palier {data.tier} · min {data.tierMinPct} %
              </Badge>
            )}
            {data.override?.by && (
              <Badge variant="outline" className="text-[10px] bg-white">
                Dérogation : {data.override.by}
              </Badge>
            )}
          </div>
          <p className="text-[11px] text-gray-600 mt-0.5">
            CA HT {fcfa(data.revenueHT)} · coût transporteur {fcfa(data.carrierCost)}
            {data.roadCosts > 0 && ` · frais de route ${fcfa(data.roadCosts)}`}
            {data.missingCarrierPrices > 0 && ` · ${data.missingCarrierPrices} camion(s) sans prix`}
          </p>
          {data.gate?.message && (
            <p className={`text-[11px] mt-1 ${tone.text}`} data-testid="margin-banner-message">
              {data.gate.message}
            </p>
          )}
        </div>
        {needsAttention && canOverride && !data.override?.by && (
          <Button
            size="sm" variant="outline" className="h-7 text-xs bg-white shrink-0"
            onClick={() => setDialogOpen(true)} data-testid="margin-override-btn"
          >
            <Unlock className="w-3 h-3 mr-1" /> Dérogation
          </Button>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full sm:max-w-md" data-testid="margin-override-dialog">
          <DialogHeader>
            <DialogTitle>Dérogation de marge</DialogTitle>
            <DialogDescription>
              {data.status === 'unknown'
                ? 'Autoriser la suite du processus malgré un coût transporteur non renseigné.'
                : `Autoriser une marge de ${data.marginPct} % (minimum du palier : ${data.tierMinPct} %).`}
              {' '}Le motif est obligatoire et conservé dans le journal d'audit.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="ex: geste commercial validé par la direction pour conserver le client sur l'axe San Pedro"
            rows={3}
            data-testid="margin-override-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Annuler</Button>
            <Button onClick={submitOverride} disabled={saving} data-testid="margin-override-confirm">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer la dérogation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OperationMarginBanner;
